import { useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from '../../lib/zod';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Button, Input } from '@workspace/ui';
import { API_URL } from '../../lib/env';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';

const createSchema = (t: TFunction<'identity'>) => z.object({
  email: z.string().email(t('auth.validation.invalidEmail')),
});

type ForgotPasswordForm = { email: string };

export function ForgotPasswordPage() {
  const { t } = useTranslation('identity');
  const schema = useMemo(() => createSchema(t), [t]);
  const navigate = useNavigate();
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const resolver = useMemo(() => zodResolver(schema), [schema]);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<ForgotPasswordForm>({
    resolver,
    defaultValues: {
      email: '',
    },
  });

  const onSubmit = handleSubmit(async (values) => {
    setError(null);
    const domain = values.email.split('@').pop()?.trim().toLowerCase() ?? '';
    if (!domain) {
      setError(t('auth.forgot.invalidEmail'));
      return;
    }

    try {
      await axios.post(`${API_URL}/auth/password/forgot`, {
        email: values.email,
        domain,
      });
    } catch {
      // Always show success to avoid user enumeration
    }

    setSubmitted(true);
  });

  if (submitted) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center bg-surface-page px-4 text-content-primary">
        <div className="w-full max-w-md rounded-lg border border-border-subtle bg-surface-primary p-8 text-center shadow-surface">
          <h1 className="mb-4 text-2xl font-semibold text-content-primary">
            {t('auth.forgot.sentTitle')}
          </h1>
          <p className="mb-6 text-content-secondary">
            {t('auth.forgot.sentDescription')}
          </p>
          <Button onClick={() => navigate('/login')} className="w-full">
            {t('auth.common.backToLogin')}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-surface-page px-4 text-content-primary">
      <div className="w-full max-w-md rounded-lg border border-border-subtle bg-surface-primary p-8 shadow-surface">
        <div className="mb-6 text-center">
          <h1 className="text-2xl font-semibold text-content-primary">
            {t('auth.forgot.title')}
          </h1>
          <p className="mt-2 text-sm text-content-muted">
            {t('auth.forgot.subtitle')}
          </p>
        </div>

        <form className="space-y-4" onSubmit={onSubmit}>
          <div>
            <label className="mb-1 block text-sm font-medium text-content-secondary">
              {t('auth.common.email')}
            </label>
            <Input type="email" placeholder="correo@empresa.com" {...register('email')} />
            {errors.email && (
              <p className="mt-1 text-xs text-state-danger">{errors.email.message}</p>
            )}
          </div>

          {error && <p className="text-sm text-state-danger">{error}</p>}

          <Button type="submit" className="w-full" disabled={isSubmitting}>
            {isSubmitting ? t('auth.forgot.sending') : t('auth.forgot.send')}
          </Button>
        </form>

        <p className="mt-4 text-center text-xs text-content-muted">
          <button
            onClick={() => navigate('/login')}
            className="text-content-accent hover:text-primary"
          >
            {t('auth.common.backToLogin')}
          </button>
        </p>
      </div>
    </div>
  );
}
